import type { GameState } from '../core/state';
import type { SaveService } from './save';
import {
  applyOfflineEarnings,
  computeOfflineEarnings,
  type OfflineResult,
} from '../core/offline';
import type { SimContext } from '../core/simulation';
import { now } from '../core/clock';
import type { EventBus } from '../core/events';

export interface LifecycleHooks {
  /** Called after offline is applied on resume (UI may show Gazette / debug). */
  onOffline?: (result: OfflineResult) => void;
}

export interface LifecycleService {
  /** Pause → autosave. */
  pause: () => Promise<void>;
  /** Resume → offline check + apply. */
  resume: () => Promise<OfflineResult>;
  /** Wire Capacitor app pause/resume when available. */
  attachNative: () => Promise<() => void>;
}

function attachVisibility(
  pause: () => Promise<void>,
  resume: () => Promise<OfflineResult>,
): () => void {
  const onVis = () => {
    if (document.visibilityState === 'hidden') void pause();
    else void resume();
  };
  document.addEventListener('visibilitychange', onVis);
  return () => document.removeEventListener('visibilitychange', onVis);
}

export function createLifecycle(
  getState: () => GameState,
  save: SaveService,
  sim: Pick<SimContext, 'economy' | 'multipliers'>,
  hooks: LifecycleHooks = {},
  events?: EventBus,
): LifecycleService {
  async function pause(): Promise<void> {
    const state = getState();
    await save.save(state);
    events?.emit('saved', { at: state.lastSavedAt });
  }

  async function resume(): Promise<OfflineResult> {
    const state = getState();
    const at = now();
    const result = computeOfflineEarnings(state, sim, { nowMs: at });
    applyOfflineEarnings(state, result, at);
    if (result.gold > 0) {
      events?.emit('goldChanged', { gold: state.currencies.gold, delta: result.gold });
    }
    events?.emit('offlineComputed', {
      gold: result.gold,
      elapsedSeconds: result.elapsedSeconds,
    });
    hooks.onOffline?.(result);
    await save.save(state);
    return result;
  }

  return {
    pause,
    resume,
    async attachNative() {
      try {
        const { Capacitor } = await import('@capacitor/core');
        if (!Capacitor.isNativePlatform()) {
          return attachVisibility(pause, resume);
        }
        const { App } = await import('@capacitor/app');
        const p = await App.addListener('pause', () => {
          void pause();
        });
        const r = await App.addListener('resume', () => {
          void resume();
        });
        return () => {
          void p.remove();
          void r.remove();
        };
      } catch {
        // Browser / node without document wiring above — visibility API fallback
        return attachVisibility(pause, resume);
      }
    },
  };
}
